import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { formatPersonName, PersonName } from "~/components/common/PersonName";
import { Loader } from "~/components/ui/loader";
import { useSearchParams } from "~/hooks/use-search-params";
import type { DateTimeStringUTC, Loading } from "~/types/common";
import type { TUser } from "~/types/user";

import { BookForm, type BookMeetingInputs } from "./book-form";
import { getTimeZone } from "./calendar-utils";
import { CalendarWidget, type CalendarWidgetProps } from "./calendar-widget";
import { LeftArrowIcon } from "./left-arrow-icon";
import type { TCreateMeeting, TExpert, TMeeting, TMeetingPatientOption } from "./meetings-types";
import { useIsRedesignMeetings } from "./redesign-meetings-context";

export function BookWidget({
  expert,
  user,
  patients,
  onBookMeeting,
  onBookMeetingSuccess,
  onBookMeetingError,
  ...calendarWidgetProps
}: {
  expert: TExpert;
  user: TUser;
  patients: Loading<TMeetingPatientOption[]>;
  onBookMeeting(data: TCreateMeeting): Promise<TMeeting>;
  onBookMeetingSuccess(meeting: TMeeting): void;
  onBookMeetingError(error: unknown): void;
} & CalendarWidgetProps) {
  const [searchParams] = useSearchParams();
  const time = searchParams.get("time");
  // The global redesign recolor covers brand tokens, but this header also contains literal legacy grays.
  const redesign = useIsRedesignMeetings();

  return (
    <div className="tw-mx-auto tw-max-w-[700px]">
      {/* The redesign card uses spacing instead of internal dividers. */}
      <div className={clsx("tw-flex tw-items-center", redesign ? undefined : "tw-border-b tw-border-[#f1f1f1]")}>
        <BackLink to={time ? `/pages/meetings/book/${expert.account_id}` : "/pages/meetings"} />

        <h2 className={clsx("tw-m-0 tw-p-4 tw-text-xs tw-font-medium sm:tw-p-[30px] sm:tw-text-base", redesign && "tw-font-sans tw-text-foreground")}>
          <FormattedMessage id={time ? "meetings.enter_details" : "meetings.select_datetime"} />
        </h2>

        <span className={clsx("tw-ml-auto tw-pr-4 max-sm:tw-text-xs sm:tw-pr-[30px]", redesign ? "tw-font-sans tw-text-muted-foreground" : "tw-text-[#adadad]")}>
          <FormattedMessage id="meetings.step" values={{ current: time ? 3 : 2, total: 3 }} />
        </span>
      </div>

      <ExpertHeader expert={expert} />

      {/* `?time` defines the step. While patient data is unavailable, keep step 3 visible with a loader. */}
      {time ? (
        patients ? (
          <BookForm
            expert={expert}
            time={time}
            patients={patients}
            defaultValues={{
              name: formatPersonName({ person: user, useMiddleName: true }),
              phone: user.phone,
              email: user.email,
            }}
            onBookMeeting={(data) => onBookMeeting(createMeetingPayload(expert, time, data))}
            onBookMeetingSuccess={onBookMeetingSuccess}
            onBookMeetingError={onBookMeetingError}
          />
        ) : (
          <div className="tw-grid tw-min-h-[300px] tw-place-items-center">
            <Loader />
          </div>
        )
      ) : (
        <CalendarWidget {...calendarWidgetProps} />
      )}
    </div>
  );
}

function BackLink({ to }: { to: string }) {
  const redesign = useIsRedesignMeetings();
  return (
    <Link
      className={clsx(
        "tw-flex tw-items-center tw-gap-1",
        redesign ? "tw-font-sans" : "tw-border-r tw-border-[#f1f1f1]",
        "tw-p-4 tw-text-base tw-text-brand-green tw-no-underline sm:tw-p-[30px]",
        "hover:tw-text-brand-green-hover active:tw-text-brand-green-hover",
      )}
      to={to}
    >
      <LeftArrowIcon width={16} height={16} />

      <span className="max-sm:tw-hidden">
        <FormattedMessage id="reminder.back" />
      </span>
    </Link>
  );
}

export function ExpertHeader({ expert }: { expert: TExpert }) {
  const redesign = useIsRedesignMeetings();
  return (
    <div className={clsx("tw-flex tw-flex-col tw-px-[30px] tw-py-[17px]", redesign ? undefined : "tw-border-b tw-border-[#f1f1f1]")}>
      <span className={clsx("tw-text-xs", redesign && "tw-font-sans tw-text-muted-foreground")}>
        <FormattedMessage id="meetings.expert" />
      </span>

      <PersonName person={expert} useMiddleName className={clsx("tw-text-xs", redesign ? "tw-font-sans tw-text-foreground" : "tw-text-[#adadad]")} />
    </div>
  );
}

function createMeetingPayload(expert: TExpert, start_time: DateTimeStringUTC, formData: BookMeetingInputs): TCreateMeeting {
  return {
    expert_id: expert.account_id,
    start_time,
    client_name: formData.name,
    client_phone: formData.phone,
    client_email: formData.email,
    client_timezone: getTimeZone(),
    patients: formData.patients.map((patient) => ({
      patient_id: Number(patient.id),
      theme: patient.theme,
      question: patient.question,
    })),
  };
}
